import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, ArrowRight, Building2, Home, Users, Clock, Zap, DollarSign, Scale, Gauge, Send, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Logo, LogoSmall } from '../components/Logo';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Commercial Inquiry Form Component
const CommercialInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_type: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/leads/commercial`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        business_type: formData.business_type,
        message: formData.message,
        interested_in: 'D-Freeze Commercial'
      });
      
      setSubmitted(true);
      toast.success('Thank you! Our commercial team will contact you within 24-48 hours.');
    } catch (error) {
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-[#0a0203] mb-2">Request Received!</h3>
        <p className="text-gray-600 mb-6">
          Our commercial team will contact you within 24-48 business hours to discuss your freeze drying needs.
        </p>
        <p className="text-sm text-gray-500">
          For immediate assistance, email us at{' '}
          <a href="mailto:info@peakprairie.ca" className="text-[#003456] hover:underline">
            info@peakprairie.ca
          </a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="commercial-inquiry-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
          <Input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Your full name"
            className="bg-white"
            data-testid="commercial-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
          <Input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="your@email.com"
            className="bg-white"
            data-testid="commercial-email"
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
          <Input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 000-0000"
            className="bg-white"
            data-testid="commercial-phone"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Type *</label>
          <select
            required
            value={formData.business_type}
            onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
            className="w-full h-10 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003456]"
            data-testid="commercial-business-type"
          >
            <option value="">Select your business type</option>
            <option value="Farm / Orchard">Farm / Orchard</option>
            <option value="Restaurant / Cafe">Restaurant / Cafe</option>
            <option value="Pet Food Business">Pet Food Business</option>
            <option value="Food Manufacturing">Food Manufacturing</option>
            <option value="Lab / Research">Lab / Research</option>
            <option value="Emergency Preparedness">Emergency Preparedness</option>
            <option value="Retail / Wholesale">Retail / Wholesale</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about your needs (optional)</label>
        <textarea
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="Describe your freeze drying needs, expected capacity, timeline, etc."
          className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#003456]"
          data-testid="commercial-message"
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={submitting}
        className="w-full bg-[#003456] hover:bg-[#002B47] text-white py-6 text-lg font-semibold"
        data-testid="commercial-submit"
      >
        {submitting ? 'Submitting...' : 'Request Commercial Consultation'}
        <Send className="ml-2 h-5 w-5" />
      </Button>
      
      <p className="text-center text-sm text-gray-500">
        By submitting, you agree to be contacted by our commercial team at{' '}
        <a href="mailto:info@peakprairie.ca" className="text-[#003456] hover:underline">
          info@peakprairie.ca
        </a>
      </p>
    </form>
  );
};

const CommercialPage = () => {
  const [selectedUseCase, setSelectedUseCase] = useState(null);

  const comparisonData = [
    { feature: 'Batch Capacity', home: '12-45 lbs per batch', commercial: '12-100 lbs per batch', icon: Scale },
    { feature: 'Tray Configuration', home: '5-7 custom trays', commercial: '5-11 trays standard sizing', icon: Scale },
    { feature: 'Total Tray Space', home: '800-2,240 sq in', commercial: '1,070-5,148 sq in', icon: Scale },
    { feature: 'Shelf Heating', home: 'Zone', commercial: 'Individual shelf heating & control', icon: Zap },
    { feature: 'Vacuum Pump', home: 'Configurable', commercial: 'Configurable', icon: Gauge },
    { feature: 'Processing Time', home: 'Approximately 24-36 hours', commercial: 'Approximately 18-24 hours', icon: Clock },
    { feature: 'Power Requirements', home: '110V 15 or 20 AMP', commercial: '110V or 220V depending on model', icon: Zap },
    { feature: 'Safety Certifications', home: 'ETL Field Certified', commercial: 'ETL Field Certified', icon: Scale },
    { feature: 'Price Range', home: 'Call for pricing', commercial: 'Call for pricing', icon: DollarSign },
    { feature: 'Annual Throughput', home: 'Up to 2,000 lbs/year', commercial: '2,000-30,000 lbs/year', icon: Gauge },
  ];

  // D-Freeze specific models
  const dFreezeModels = [
    {
      name: 'D-Freeze 5 Half Tray Unit',
      tagline: 'Entry Commercial',
      description: 'Perfect for small businesses transitioning from home freeze drying. Features the same quality components as larger units in a more accessible package.',
      specs: [
        { label: 'Trays', value: '5 half-size (13×18")' },
        { label: 'Tray Material', value: 'Aluminum or Stainless Steel' },
        { label: 'Vacuum Pump', value: '7 CFM' },
        { label: 'Shelf Heating', value: 'Individual per tray' },
        { label: 'Certification', value: 'ETL Electrical Safety' },
        { label: 'Best For', value: 'Small businesses, market vendors' }
      ],
      link: '/contact',
      featured: false
    },
    {
      name: 'D-Freeze 5 Full Tray Unit',
      tagline: 'Full Commercial Power',
      description: 'Maximum capacity with a compact footprint. Designed for serious food businesses requiring high throughput and professional-grade performance.',
      specs: [
        { label: 'Trays', value: '5 full-size (18×26×1")' },
        { label: 'Total Tray Space', value: '2,340 sq in' },
        { label: 'Vacuum Pump', value: 'Commercial grade' },
        { label: 'Shelf Heating', value: 'Independent monitoring' },
        { label: 'Processing', value: 'Faster cycle times' },
        { label: 'Best For', value: 'Production facilities, wholesale' }
      ],
      link: '/contact',
      featured: true
    }
  ];

  const useCases = [
    {
      id: 'hobby',
      title: 'Hobby & Personal Use',
      description: 'Preserving food for your family, making snacks, emergency preparedness',
      recommendation: 'home',
      icon: Home,
      details: 'A D-Freeze home freeze dryer is perfect for you. Process 12-45 lbs per batch, run it in your kitchen or garage, and enjoy the convenience of home freeze drying.'
    },
    {
      id: 'side-hustle',
      title: 'Side Business / Farmers Market',
      description: 'Selling at local markets, small online store, pet treat business',
      recommendation: 'home-multiple',
      icon: Users,
      details: 'Start with a D-Freeze home unit. Many successful small businesses run 2-3 home units before upgrading to commercial. This lets you grow gradually with lower upfront investment.'
    },
    {
      id: 'small-business',
      title: 'Small Commercial Operation',
      description: 'Dedicated production facility, wholesale accounts, retail distribution',
      recommendation: 'commercial',
      icon: Building2,
      details: 'You need commercial equipment. D-Freeze offers commercial and industrial freeze dryers designed for continuous production, food safety compliance, and commercial-scale output.'
    },
    {
      id: 'large-scale',
      title: 'Large-Scale Manufacturing',
      description: 'Food manufacturing, pharmaceutical, industrial applications',
      recommendation: 'commercial-enterprise',
      icon: Building2,
      details: 'Our recommendation: Commercial/Industrial Freeze Dryer. D-Freeze provides enterprise solutions with custom configurations, installation support, and ongoing maintenance for large-scale operations.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#003456] to-[#002B47]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-md mb-6">
              <Building2 className="h-5 w-5 text-[#CE8946]" />
              <span className="text-white/90 font-medium">Home vs. Commercial</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Which Freeze Dryer is <span className="text-[#CE8946]">Right for You?</span>
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Whether you're preserving food for your family or scaling up a food business, we'll help you find the perfect solution. D-Freeze for home use, D-Freeze for commercial operations.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-8 py-6 text-lg">
                  <Home className="mr-2 h-5 w-5" />
                  Home Freeze Dryers
                </Button>
              </Link>
              <a href="#commercial-form">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                  <Building2 className="mr-2 h-5 w-5" />
                  Commercial Solutions
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Finder */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Find Your Perfect Solution</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Select your use case to get a personalized recommendation.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {useCases.map((useCase) => (
              <button
                key={useCase.id}
                onClick={() => setSelectedUseCase(useCase)}
                className={`p-6 rounded-2xl text-left transition-all ${
                  selectedUseCase?.id === useCase.id
                    ? 'bg-[#003456] text-white shadow-xl scale-105'
                    : 'bg-white hover:shadow-lg hover:scale-102'
                }`}
              >
                <useCase.icon className={`h-10 w-10 mb-4 ${selectedUseCase?.id === useCase.id ? 'text-[#CE8946]' : 'text-[#003456]'}`} />
                <h3 className={`text-lg font-bold mb-2 ${selectedUseCase?.id === useCase.id ? 'text-white' : 'text-[#0a0203]'}`}>
                  {useCase.title}
                </h3>
                <p className={`text-sm ${selectedUseCase?.id === useCase.id ? 'text-white/80' : 'text-gray-600'}`}>
                  {useCase.description}
                </p>
              </button>
            ))}
          </div>

          {/* Recommendation Display */}
          {selectedUseCase && (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl animate-fade-in">
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  selectedUseCase.recommendation.includes('commercial') ? 'bg-[#12757D]/20' : 'bg-[#CE8946]/20'
                }`}>
                  {selectedUseCase.recommendation.includes('commercial') ? (
                    <Building2 className="h-8 w-8 text-[#003456]" />
                  ) : (
                    <Home className="h-8 w-8 text-[#CE8946]" />
                  )}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#0a0203] mb-2">
                    Our Recommendation: {selectedUseCase.recommendation.includes('commercial') ? 'Commercial Freeze Dryer' : 'Home Freeze Dryer'}
                  </h3>
                  <p className="text-gray-600">{selectedUseCase.details}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                {selectedUseCase.recommendation.includes('commercial') ? (
                  <>
                    <a href="#commercial-form">
                      <Button className="bg-[#003456] hover:bg-[#002B47] text-white px-8 py-6">
                        Explore Commercial Solutions
                      </Button>
                    </a>
                    <a href="#commercial-form">
                      <Button variant="outline" className="border-[#003456] text-[#003456] px-8 py-6">
                        Request Commercial Quote
                      </Button>
                    </a>
                  </>
                ) : (
                  <>
                    <Link to="/products">
                      <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] px-8 py-6">
                        Shop D-Freeze Freeze Dryers
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/compare">
                      <Button variant="outline" className="border-[#003456] text-[#003456] px-8 py-6">
                        Compare Home Models
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Home vs. Commercial: Key Differences</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Understanding the differences will help you make the right choice for your needs.</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-6 font-bold text-[#0a0203]">Feature</th>
                  <th className="text-center py-4 px-6">
                    <div className="flex flex-col items-center gap-2">
                      <Home className="h-8 w-8 text-[#CE8946]" />
                      <span className="font-bold text-[#0a0203]">Home (D-Freeze)</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-6">
                    <div className="flex flex-col items-center gap-2">
                      <Building2 className="h-8 w-8 text-[#003456]" />
                      <span className="font-bold text-[#0a0203]">Commercial (D-Freeze)</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-[#F8FAFC]' : 'bg-white'}>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <row.icon className="h-5 w-5 text-[#003456]" />
                        <span className="font-medium text-[#0a0203]">{row.feature}</span>
                      </div>
                    </td>
                    <td className="text-center py-4 px-6 text-gray-700">{row.home}</td>
                    <td className="text-center py-4 px-6 text-gray-700">{row.commercial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* When to Choose Each */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Home Column */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#CE8946]/20 rounded-2xl flex items-center justify-center">
                  <Home className="h-8 w-8 text-[#CE8946]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#0a0203]">Choose D-Freeze When...</h3>
                  <p className="text-gray-600">Home freeze dryers are perfect for:</p>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Processing food for your family',
                  'Building an emergency food supply',
                  'Making healthy snacks and pet treats',
                  'Preserving garden harvests',
                  'Starting a small side business',
                  'Running quietly in your home',
                  'Standard household electrical outlets'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/products">
                <Button className="w-full bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold py-6">
                  Shop Home Freeze Dryers
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            {/* Commercial Column */}
            <div className="bg-[#003456] rounded-3xl p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-[#CE8946]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Choose D-Freeze When...</h3>
                  <p className="text-white/70">Commercial freeze dryers are needed for:</p>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Repeatable product quality',
                  'Running a dedicated production facility or commercial kitchen',
                  'Wholesale or retail distribution',
                  'Food safety compliance required',
                  'Continuous production schedules',
                  'Industrial-scale manufacturing',
                  'Pharmaceutical or scientific applications'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#CE8946] mt-0.5 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
              <a href="#commercial-form" className="block">
                <Button className="w-full bg-white hover:bg-gray-100 text-[#003456] font-semibold py-6">
                  Explore Commercial Solutions
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* D-Freeze Models Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#003456]/10 px-4 py-2 rounded-md mb-6">
              <Building2 className="h-5 w-5 text-[#003456]" />
              <span className="text-[#003456] font-medium">D-Freeze Commercial Models</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">
              Detailed Specifications
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              D-Freeze offers commercial freeze dryers with individual shelf heating, high-performance vacuum pumps, and ETL safety certification.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {dFreezeModels.map((model, i) => (
              <div key={i} className={`rounded-3xl p-8 ${model.featured ? 'bg-[#003456] text-white' : 'bg-[#F8FAFC]'}`}>
                <div className={`inline-block px-3 py-1 rounded text-sm font-semibold mb-4 ${model.featured ? 'bg-[#CE8946] text-[#0a0203]' : 'bg-[#003456]/10 text-[#003456]'}`}>
                  {model.tagline}
                </div>
                <h3 className={`text-2xl font-bold mb-3 ${model.featured ? 'text-white' : 'text-[#0a0203]'}`}>
                  {model.name}
                </h3>
                <p className={`mb-6 ${model.featured ? 'text-white/80' : 'text-gray-600'}`}>
                  {model.description}
                </p>
                
                {/* Specs Table */}
                <div className={`rounded-xl overflow-hidden mb-6 ${model.featured ? 'bg-white/10' : 'bg-white'}`}>
                  {model.specs.map((spec, j) => (
                    <div key={j} className={`flex justify-between py-3 px-4 ${j !== model.specs.length - 1 ? (model.featured ? 'border-b border-white/10' : 'border-b border-gray-100') : ''}`}>
                      <span className={model.featured ? 'text-white/70' : 'text-gray-500'}>{spec.label}</span>
                      <span className={`font-medium ${model.featured ? 'text-white' : 'text-[#0a0203]'}`}>{spec.value}</span>
                    </div>
                  ))}
                </div>
                
                <a href={model.link} className="block">
                  <Button className={`w-full py-6 ${model.featured ? 'bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203]' : 'bg-[#003456] hover:bg-[#002B47] text-white'}`}>
                    Request a Quote
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            ))}
          </div>

          {/* Key Features */}
          <div className="bg-gradient-to-r from-[#003456] to-[#002B47] rounded-3xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">D-Freeze Key Advantages</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Individual Shelf Heating',
                  description: 'Each tray has independent heating and monitoring. If one heating element fails, it can be replaced without affecting others.'
                },
                {
                  title: 'ETL Safety Certified',
                  description: 'All D-Freeze units are ETL certified for electrical safety, meeting commercial kitchen and facility requirements.'
                },
                {
                  title: 'High-Performance Pumps',
                  description: '7+ CFM vacuum pumps provide faster evacuation and more consistent freeze drying results for commercial batches.'
                }
              ].map((feature, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-[#CE8946] rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Check className="h-6 w-6 text-[#0a0203]" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{feature.title}</h4>
                  <p className="text-white/70 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="#commercial-form">
                <Button className="bg-white hover:bg-gray-100 text-[#003456] px-8 py-6">
                  Talk to Our Commercial Team
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Inquiry Form */}
      <section className="py-20 bg-white" id="commercial-form">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">
              Request Commercial Information
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Interested in D-Freeze commercial solutions? Fill out the form below and our commercial team will contact you within 24-48 hours.
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-3xl p-8 md:p-12">
            <CommercialInquiryForm />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0a0203] text-center mb-12">Common Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: "Can I start with a home freeze dryer and upgrade later?",
                a: "Absolutely! Many successful food businesses started with 1-3 D-Freeze home units before upgrading to commercial equipment. This allows you to test your market and grow gradually with lower initial investment."
              },
              {
                q: "What's the main difference in output capacity?",
                a: "Home units produce 15-45 lbs per batch, while commercial machines can produce 30-100 lbs per batch with consistent repeatable quality. A home unit can process up to 2,000 lbs per year with regular use, while commercial units can process 2,000-30,000 lbs annually."
              },
              {
                q: "Why is there a price difference between home and commercial freeze dryers?",
                a: "Commercial freeze dryers are built with higher end electrical components and are subject to higher duty rates than home freeze dryers. While home freeze dryers can be used for business, many of the parts are designed to support non-continuous use, whereas commercial machinery is designed for continuous use."
              },
              {
                q: "Do I need special permits for commercial freeze drying?",
                a: "Yes, commercial food production typically requires health department permits, food handling certifications, and possibly FDA registration depending on what you're producing. D-Freeze can help guide you through compliance requirements."
              },
              {
                q: "Can I use a home freeze dryer for a small business?",
                a: "Yes, many cottage food businesses successfully use home freeze dryers. However, check your local cottage food laws, as regulations vary by state. Some allow home-processed foods for direct sales, while others require commercial kitchens."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#0a0203] mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#003456]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Still Not Sure Which is Right for You?</h2>
          <p className="text-white/80 mb-8 text-lg">Our team can help you evaluate your needs and find the perfect freeze drying solution.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-8 py-6 text-lg">
                Talk to an Expert
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/compare">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                Compare Home Models
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommercialPage;
